// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { storage } from '../services/storage';
import { setCookie, getCookie, removeCookie } from '../services/cookieService';

// Set this to true to bypass login (development mode)
const DEV_MODE = false;

// Cookie name for storing user data
const USER_COOKIE = 'faraone_user_data';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(DEV_MODE);

    useEffect(() => {
        const initAuth = () => {
            if (DEV_MODE) {
                // Development mode: auto-login with dev user
                setUser({ id: 1, name: 'Dev User', role: 'admin' });
                setIsAuthenticated(true);
            } else {
                // Production mode: normal authentication flow
                // Try to get user from cookie first, then fallback to localStorage for backward compatibility
                const userCookie = getCookie(USER_COOKIE);
                let savedUser = null;

                if (userCookie && userCookie !== 'undefined' && userCookie !== 'null') {
                    try {
                        savedUser = JSON.parse(userCookie);
                    } catch (e) {
                        console.error('Error parsing user cookie:', e);
                        // Remove invalid cookie
                        removeCookie(USER_COOKIE);
                    }
                } else if (userCookie) {
                    // Remove invalid cookie (contains "undefined" or "null")
                    removeCookie(USER_COOKIE);
                }

                // Fallback to localStorage if no cookie found
                if (!savedUser) {
                    savedUser = storage.loadState()?.user;

                    // If user found in localStorage, migrate it to cookie
                    if (savedUser) {
                        setCookie(USER_COOKIE, JSON.stringify(savedUser));
                    }
                }

                if (savedUser) {
                    setUser(savedUser);
                    setIsAuthenticated(true);
                }
            }
            setIsLoading(false);
        };

        initAuth();
    }, []);

    const login = async (credentials) => {
        setIsLoading(true);
        try {
            const response = await api.login(credentials);
            const userData = response.user;

            setUser(userData);
            setIsAuthenticated(true);

            // Save to both cookie and localStorage for backward compatibility
            // IMPORTANT: Preserve existing data (like cartItems) by merging with current storage
            setCookie(USER_COOKIE, JSON.stringify(userData));
            const currentStorage = storage.loadState() || {};
            storage.saveState({ ...currentStorage, user: userData });

            return userData;
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        setUser(null);
        setIsAuthenticated(false);

        // Clear both cookie and localStorage
        removeCookie(USER_COOKIE);
        storage.clearState();
    };

    const value = {
        user,
        isLoading,
        isAuthenticated,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
